import {
  AccessoryOrderRecord,
  LineItem,
  Order,
  SharedDesignOrigin
} from "../order/order";
import {
  CustomBaseOrder,
  CustomChairOrder,
  CustomUmbrellaOrder
} from "../configurator/custom_umbrella";
import { NetSuiteQuoteLineItem } from "../netsuite/netsuite";
import { ProductType } from "../products/product";
import { NoteFields } from "../order/helpers";
import {
  sharedDesignAdjusted,
  sharedDesignCode,
  sharedDesignOrigin
} from "@/utility/shared_design";
import { setupIdOf } from "@/utility/line_setup";

/**
 * FUPOF-191 — Quote drafts persisted in the Laravel API's PostgreSQL DB.
 * These interfaces are the source-of-truth contract for the /api/quotes/*
 * endpoints (see .claude/context/FUPOF-187/FUPOF-191/CLIENT-PAYLOAD-CONTRACT.md).
 * API/DB fields use snake_case per project convention.
 */

// Mirrors the Laravel App\Enums\QuoteStatus lifecycle. The former "submitted"
// value was renamed to "open" to match NetSuite naming; "pending_sales_order",
// "processed", and "expired" track the rest of the quote's life after submit.
export enum QuoteDraftStatus {
  Draft = "draft",
  Open = "open",
  PendingSalesOrder = "pending_sales_order",
  Processed = "processed",
  Expired = "expired"
}

/** Everything on the Order except line items and ids owned by dedicated columns. */
export type QuoteDraftHeader = Pick<
  Order,
  | "contact_details"
  | "shipping_details"
  | "billing_details"
  | "payment_details"
  | "notes"
  | "cost_details"
  // FUPOF-209: last-known NetSuite shipping + tax. Persisted so a reopened quote can
  // show them before (or without) a successful reload — NOT a rep-authored field.
  | "ns_totals"
  | "customer_id"
  | "customer_name"
  | "customer_netsuite_contact_id"
  | "customer_netsuite_payment_terms_id"
  | "customer_netsuite_price_level_id"
  | "customer_netsuite_price_level_discount"
>;

export interface QuoteDraftLine {
  /** Minted (or reused via config content-match) by the API on save; null until first save. */
  uuid: string | null;
  /** NetSuite internal line id; set by the API after submit. */
  ns_line_id: number | null;
  position: number;
  qty: number;
  unit_price: number;
  multiplier: number;
  notes: string;
  product_type: ProductType;
  sku: string | null;
  model: string;
  display_name: string;
  product_line: string;
  /** LineItem.type (drop ship, quickship, etc.) */
  line_type: string | null;
  /** LineItem._id — differentiates quickship items */
  quickship_id: string | null;
  item_details:
    | CustomUmbrellaOrder
    | CustomBaseOrder
    | CustomChairOrder
    | AccessoryOrderRecord
    | null;
  /** True when item_details was best-effort reconstructed by SkuParserService. */
  parsed?: boolean;
  /**
   * Frontend-only: line adopted from NetSuite (orphan or Accept-NS resolution).
   * Configurator is locked; only qty/price/notes are editable.
   */
  ns_locked?: boolean;
  /**
   * FUPOF-200: why this reordered line could not be repriced or reconfigured.
   * The API emits the key only when it is set, so every pre-FUPOF-200 payload
   * stays byte-identical.
   */
  stale_reason?: StaleLineReason;
}

/** FUPOF-200 — why a reordered line could not be repriced or reconfigured. */
export enum StaleLineReason {
  /** Parser gave up: discontinued or renamed SKU, no product type, no config. */
  SkuNotRecognized = "sku_not_recognized",
  /** TEMP-CUST-ITEM line; its MSRP is the source order's, with nothing to reprice against. */
  CustomLineHistoricalPrice = "custom_line_historical_price",
  /** Catalog item has no Base Price row today; the price came from the order's own arithmetic. */
  NoCurrentBasePrice = "no_current_base_price"
}

export interface StaleLine {
  position: number;
  display_name: string;
  reason: StaleLineReason;
  reason_label: string;
}

/**
 * Response of POST /api/quotes/sales-orders/duplicate/preview. Unlike every
 * other quote endpoint this is an envelope, not a bare quote — the caller needs
 * `stale_lines` to tell the rep how many lines want a look before submitting.
 */
export interface SalesOrderDuplicatePreview {
  quote: QuoteDraft;
  source_sales_order: {
    id: number;
    number: string | null;
    date: string | null;
  };
  stale_lines: StaleLine[];
}

/**
 * A row from GET /api/quotes/sales-orders — the caller's duplicatable Sales
 * Orders, scoped server-side to their effective rep ids and returned newest
 * first (the API sorts and pages; the client only searches).
 *
 * Deliberately its own type rather than an `Order`: the quote-shaped mixin
 * helpers (sortedOrders, tabCounts, filteredOrders, effectiveStatus) all assume
 * quote semantics, and coercing Sales Orders through Order is how
 * convertNetSuiteQuotes ended up with its FNSI-101 TODOs.
 *
 * `status` here is the pre-stripped SO status ("Pending Fulfillment", "Billed",
 * "Closed"). Note the near-collision with `Order.netsuite_sales_order_status`
 * from the linked-sales-order payload — different responses, don't share a type.
 */
export interface SalesOrderSummary {
  sales_order_id: number;
  sales_order_number: string | null;
  sales_order_date: string | null;
  /** null when the customer row didn't join (the API uses a LEFT JOIN by design). */
  customer_id: number | null;
  customer_name: string | null;
  po_num: string | null;
  total: number | null;
  status: string | null;
}

/**
 * One page of GET /api/quotes/sales-orders, plus how many match in total.
 *
 * Paged by the API rather than fetched whole: a single rep can have 7,500+
 * sales orders (57,681 account-wide in Sandbox, 2026-09-29), more than one
 * NetSuite read returns. `total` counts the whole match, not this page.
 */
export interface SalesOrderListResult {
  sales_orders: SalesOrderSummary[];
  total: number;
  page: number;
  per_page: number;
}

export interface QuoteDraft {
  id: number;
  /** The draft's creator. Governs who may delete it; teammates share view/edit. */
  user_id?: number;
  /** The creator's display name, returned by the list endpoint for team labelling. */
  owner_name?: string;
  netsuite_customer_id: number | null;
  /** Set once the draft has been submitted to NetSuite. */
  netsuite_quote_id: number | null;
  title: string;
  status: QuoteDraftStatus;
  header_payload: QuoteDraftHeader;
  last_synced_at: string | null;
  created_at?: string;
  updated_at?: string;
  lines: QuoteDraftLine[];
}

/** Body for POST /quotes and PUT /quotes/{id} (replace-all on lines). */
export interface QuoteDraftPayload {
  netsuite_customer_id: number | null;
  title: string;
  header_payload: QuoteDraftHeader;
  lines: QuoteDraftLine[];
}

/** Response of POST /quotes/{id}/submit. Mirrors the create_quote response shape. */
export interface SubmitQuoteDraftResponse {
  status: string; // "success" on success, matching the existing create_quote contract
  quote: QuoteDraft;
  estimate?: {
    estimateId: number;
    tranId: string;
  };
}

export type DriftField =
  | "sku"
  | "qty"
  | "unit_price"
  | "notes"
  | "multiplier"
  | "orphan";

/**
 * How the API tied this NetSuite line back to a PSQL line, in descending
 * confidence: by the line's own uuid, by its configuration's uuid (identity was
 * lost in NS but the config still matches — a recovered line, not a new one), or
 * not at all, in which case the config is best-effort parsed from the SKU.
 */
export type DriftMatch = "line_uuid" | "config_uuid" | "sku_parse";

export interface DriftLine {
  /** null for orphan lines (added directly in NetSuite, no UUID). */
  uuid: string | null;
  ns_line: NetSuiteQuoteLineItem | null;
  /** For orphans this is the best-effort SKU-parsed hydration (parsed: true). */
  psql_line: QuoteDraftLine | null;
  drift: DriftField[];
  /** Optional: older API responses omit it — treat a missing value as unknown. */
  match?: DriftMatch;
}

/** NetSuite-owned amounts that never reach the cart as line items. See `ns_totals`. */
export interface NsTotals {
  shipping: number;
  tax: number;
}

/** Response of GET /quotes/{id}/reload. Header drift is auto-applied (NS wins). */
export interface DriftReport {
  /**
   * The NS estimate's current linked customer (entity), always present. The
   * customer_* block only appears in `header_drift` when the entity drifted from
   * the draft's stored customer, but this id always reflects NS's live entity so
   * the store's `customer_netsuite_id` can be kept true on every reload.
   * Optional here: older API responses omit it — treat a missing value as unknown.
   */
  netsuite_customer_id?: number | null;
  header_drift: Partial<QuoteDraftHeader> | null;
  /**
   * FUPOF-209: shipping and tax as NetSuite holds them on the estimate. Neither is
   * derivable here — shipping is keyed in by Frankford after a rep requests a freight
   * quote, tax is AvaTax's — and neither arrives as a line item, so without this the
   * Quote Summary can only say TBD.
   *
   * Null means the answer is unknown (the API's NetSuite read failed, an older API
   * omits the key, or the drift simulator produced the report); zeroes mean NetSuite
   * genuinely holds neither yet. Both render as TBD, but only zeroes are an answer.
   *
   * Subtotal is deliberately absent: NetSuite's includes the surcharge line, which
   * this app owns and recalculates from the cart, so adding both would double-count.
   */
  ns_totals?: NsTotals | null;
  lines: DriftLine[];
  /** Lines deleted in NetSuite; informational/read-only in the UI. */
  removed_lines: QuoteDraftLine[];
}

/** Per-line staged choice in the DriftResolver UI. */
export enum DriftResolution {
  KeepMine = "keep_mine",
  AcceptNs = "accept_ns"
}

export const toQuoteDraftLine = (
  item: LineItem,
  position: number
): QuoteDraftLine => ({
  uuid: item.uuid ?? null,
  ns_line_id: item.ns_line_id ?? null,
  position,
  qty: item.qty,
  unit_price: item.unit_price,
  multiplier: item.multiplier,
  // The line's free text, and only that. NetSuite's `custcol_productionnotes`
  // holds the *composed* string (free text + the configurator's field notes,
  // see composeLineNotes), so the two are structurally never equal and the
  // API's reload reports notes drift on every line. That drift is parked
  // client-side (see reloadFromNs) rather than papered over by storing the
  // composed string here — writing a composed value back into one of its own
  // inputs is what made the Accept-NS bug possible in the first place.
  notes: item.notes ?? "",
  product_type: item.product_type,
  // Accessories have no composed SKU — model and SKU are the same value. New
  // lines set both at creation (AccessoryMenu.addNewLineItem), but carts and
  // drafts saved before that carry a blank sku, so fall back to the model here
  // too and the API never receives an accessory line without a SKU.
  sku:
    item.sku ||
    (item.product_type === ProductType.Accessory ? item.model : null) ||
    null,
  model: item.model,
  display_name: item.display_name,
  product_line: item.product_line,
  line_type: item.type ?? null,
  quickship_id: item._id ?? null,
  // FUPOF-193: fold the shared-design marker, its adjusted flag and the setup
  // id into item_details on the way out. The Laravel API whitelists line keys
  // explicitly (StoreQuoteRequest, QuoteController::lineInstanceFields) and
  // drops anything else, but stores item_details verbatim — so this is the only
  // place any of them can ride along without a migration in that repo.
  item_details:
    item.sd_share_id || item.sd_adjusted || item.setup_id
      ? ({
          ...((item.item_details ?? {}) as object),
          ...(item.sd_share_id ? { sd_share_id: item.sd_share_id } : {}),
          ...(item.sd_adjusted ? { sd_adjusted: true } : {}),
          ...(item.setup_id ? { setup_id: item.setup_id } : {})
        } as QuoteDraftLine["item_details"])
      : item.item_details ?? null,
  ...(item.ns_locked ? { ns_locked: true } : {}),
  // Round-tripped so a reconstructed configuration keeps saying so across a
  // save/reload; the API only sets it on the way out (drift orphans), never
  // reads it back off a line, so this is purely for the client's own hydration.
  ...(item.ns_parsed ? { parsed: true } : {})
  // FUPOF-200: `stale_reason` is deliberately NOT sent back. Unlike `parsed` it
  // has no column behind it — staleness is transient by design (no migration),
  // so the API would discard the key and never return it on a reload. Sending
  // it would buy nothing and risk a 422 against a strict line rule set. The
  // reorder pill therefore lives only as long as the un-saved preview in the
  // cart, which is the window the rep is meant to review it in.
});

/**
 * An ns_locked line — an NS-added line the SKU parser gave up on — carries
 * NetSuite's production-notes string on `notes`, and either no item_details at
 * all or a bare stub (`{ netsuite_memo }`); either way its notes have nowhere
 * structured to live. Left as-is that string would be invisible in the cart —
 * the notes modal only edits item_details.notes, never the line's free text.
 *
 * An ns_locked line has no configurator, so there is nowhere to show per-field
 * notes against and (by request) no per-field boxes are created: NetSuite's
 * value — which can be any free-form text — is dropped **verbatim, unaltered**
 * into the single catch-all General block so no data is lost and the rep sees
 * exactly what the quote held. It is merged onto any existing item_details so a
 * stub's other fields (e.g. netsuite_memo) survive. On submit `composeLineNotes`
 * sends an ns_locked line's notes back with no "X Notes:" heading, so this
 * verbatim value round-trips through NetSuite unchanged — no wrapper accretes.
 * Only fires for ns_locked lines whose `notes` still holds text; once recovered
 * `notes` is emptied, so re-loading the saved line is a no-op, and non-locked
 * lines (normal drafts, parsed configs) are never touched.
 */
const recoverLockedLineNotes = (
  line: QuoteDraftLine
): { notes: string; item_details: LineItem["item_details"] } | null => {
  if (!line.ns_locked || !line.notes) return null;
  return {
    notes: "",
    item_details: {
      ...(line.item_details ?? {}),
      notes: [{ field: NoteFields.General, content: line.notes }]
    } as LineItem["item_details"]
  };
};

// Laravel serializes decimal columns as strings ("1710.00") and may omit
// fields it doesn't persist — coerce/default so hydration never yields NaN
// totals or undefined display fields.
export const toLineItem = (line: QuoteDraftLine): LineItem => {
  const recovered = recoverLockedLineNotes(line);
  return {
    ...(line.quickship_id ? { _id: line.quickship_id } : {}),
    ...(line.line_type ? { type: line.line_type } : {}),
    product_type: line.product_type,
    display_name: line.display_name ?? "",
    product_line: line.product_line ?? "",
    sku: line.sku ?? undefined,
    model: line.model ?? "",
    qty: Number(line.qty),
    unit_price: Number(line.unit_price),
    // Free text in, free text out — `notes` is never the composed string, so
    // there is nothing to take apart here. Laravel's ConvertEmptyStringsToNull
    // rewrites an empty string to null on the way in, hence the ?? "". The one
    // exception is a recovered ns_locked line (see recoverLockedLineNotes).
    notes: recovered ? recovered.notes : line.notes ?? "",
    item_details: recovered
      ? recovered.item_details
      : line.item_details ?? undefined,
    multiplier: line.multiplier != null ? Number(line.multiplier) : 1,
    uuid: line.uuid ?? undefined,
    ns_line_id: line.ns_line_id ?? undefined,
    ...(line.ns_locked ? { ns_locked: true } : {}),
    ...(line.parsed ? { ns_parsed: true } : {}),
    // FUPOF-193: lift the marker back onto the line so the cart pill works off
    // one field regardless of whether the line came from the store or a draft.
    ...(sharedDesignCode(line as any)
      ? { sd_share_id: sharedDesignCode(line as any) as string }
      : {}),
    ...(sharedDesignAdjusted(line as any) ? { sd_adjusted: true } : {}),
    ...(sharedDesignOrigin(line as any)
      ? {
          sd_origin: sharedDesignOrigin(line as any) as SharedDesignOrigin
        }
      : {}),
    // Same for the setup id, so grouping and the setup editor read one field.
    ...(setupIdOf(line as any)
      ? { setup_id: setupIdOf(line as any) as string }
      : {}),
    ...(line.stale_reason ? { stale_reason: line.stale_reason } : {})
  };
};
